import { Box, SxProps, Theme, Typography } from '@mui/material';
import { FC, useMemo } from 'react';

import { useScheduleGridContext } from './ScheduleGridContext';
import { formatMinutesToTime, getGridBodyHeight, getMinutesSinceMidnight } from './utils';
import { SystemStyleObject } from '@mui/system';

export const TIME_COLUMN_Z_INDEX = 5;

export interface ScheduleGridTimeColumnProps {
  sx?: SxProps<Theme>;
  hideLastLabel?: boolean;
}

export const ScheduleGridTimeColumn: FC<ScheduleGridTimeColumnProps> = ({
  sx,
  hideLastLabel = true,
}) => {
  const { hourHeight, timeRange } = useScheduleGridContext();

  const { totalHeight, hourMarkers } = useMemo(() => {
    const height = getGridBodyHeight(timeRange, hourHeight);
    const startMinutes = getMinutesSinceMidnight(timeRange.start);
    const endMinutes = getMinutesSinceMidnight(timeRange.end);
    const totalHours = (endMinutes - startMinutes) / 60;

    const hourIndices = Array.from(
      { length: Math.max(0, Math.floor(totalHours) - (hideLastLabel ? 1 : 0)) },
      (_, i) => i + 1,
    );
    const markers = hourIndices.reduce<{ time: string; top: number }[]>(
      (acc, hourIndex) => [
        ...acc,
        {
          time: formatMinutesToTime(startMinutes + hourIndex * 60),
          top: hourIndex * hourHeight,
        },
      ],
      [],
    );

    return { totalHeight: height, hourMarkers: markers };
  }, [timeRange, hourHeight, hideLastLabel]);

  return (
    <Box
      sx={(theme) => ({
        position: 'sticky',
        left: 0,
        zIndex: TIME_COLUMN_Z_INDEX,
        backgroundColor: theme.palette.background.paper,
        borderRight: theme.mixins.borderValue(),
        minWidth: 48,
        height: totalHeight,
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
    >
      {hourMarkers.map(({ time, top }) => (
        <Box
          key={time}
          sx={(theme) => ({
            position: 'absolute',
            top,
            right: 0,
            left: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: theme.spacing(0.75),
              borderTop: theme.mixins.borderValue(),
            },
          })}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.common.grey,
              paddingRight: theme.spacing(1),
              textAlign: 'right',
              transform: 'translateY(-50%)',
            })}
          >
            {time}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
